import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Activity, ArrowLeft, ChevronRight, CookingPot, Dumbbell, Languages, MapPin,
  BellRing, FileArchive, PackageCheck, ShieldCheck, ShoppingBasket, Truck, UserRound, Utensils,
} from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { accountSectionFromParams, accountSectionHref } from "../lib/playerAccountSections.mjs";
import AccountProfileSettings from "./AccountProfileSettings";
import PlayerInventoryAccount from "./PlayerInventoryAccount";
import PlayerNutritionAccount from "./PlayerNutritionAccount";
import PlayerPerformanceHistory from "./PlayerPerformanceHistory";
import PlayerDocumentsBilling from "./PlayerDocumentsBilling";
import PlayerTrainingPlans from "./PlayerTrainingPlans";
import PlayerPushSettings from "./PlayerPushSettings";
import PlayerMonthlyCosts from "./PlayerMonthlyCosts";

export const ACCOUNT_GROUPS = [
  { de: "Persönlich", en: "Personal", sections: ["profile"] },
  { de: "Ernährung & Alltag", en: "Nutrition & everyday life", sections: ["nutrition", "shopping", "inventory", "deliveries", "household"] },
  { de: "Training & Leistung", en: "Training & performance", sections: ["training-plan", "performance"] },
  { de: "Dokumente & Orte", en: "Documents & places", sections: ["documents", "addresses"] },
  { de: "Einstellungen", en: "Settings", sections: ["notifications", "appearance", "security"] },
];
const nutritionSections = ["household", "nutrition", "shopping"];
const inventorySections = ["inventory", "deliveries", "addresses"];
const familyOf = (section) => nutritionSections.includes(section) ? "nutrition"
  : inventorySections.includes(section) ? "inventory" : section;

// A player change discards cached forms and pending responses from the old view.
export default function PlayerAccount(props) {
  return <AccountWorkspace key={props.user?.id || "anonymous"} {...props} />;
}

function AccountWorkspace({ user, theme, setTheme }) {
  const { t, lang } = useI18n();
  const l = (de, en) => lang === "de" ? de : en;
  const [searchParams] = useSearchParams();
  const section = accountSectionFromParams(searchParams);
  const location = useLocation();
  const navigate = useNavigate();
  const requestedOrigin = location.state?.performanceOrigin;
  const performanceOrigin = section === "performance" && /^\/today(?:\?|$)/.test(requestedOrigin || "")
    && !new URLSearchParams(requestedOrigin.split("?")[1]).has("section") ? requestedOrigin : null;
  const backHref = performanceOrigin || accountSectionHref();
  const fromToday = performanceOrigin && new URLSearchParams(performanceOrigin.split("?")[1]).get("tab") !== "account";
  const reviewEntry = section === "nutrition" ? [searchParams.get("review"), searchParams.get("version"), searchParams.get("history")].join(":") : "";
  const family = familyOf(section);
  const [visited, setVisited] = useState(() => family ? [family] : []);
  // Include a newly opened view in this commit, before restoring scroll/focus.
  const mountedFamilies = family && !visited.includes(family) ? [...visited, family] : visited;
  const [training, setTraining] = useState(null);
  const titleRef = useRef(null);
  const overviewPosition = useRef(0);
  const lastEntry = useRef(null);
  const overviewRef = useRef(null);

  const loadTraining = useCallback(async () => {
    try { const { data } = await api.get("/me/training-plans"); setTraining(data); }
    catch { /* The training detail retains its existing error state. */ }
  }, []);
  useEffect(() => { loadTraining(); }, [loadTraining]);
  useEffect(() => {
    if (family) setVisited((current) => current.includes(family) ? current : [...current, family]);
  }, [family]);
  useLayoutEffect(() => {
    window.scrollTo({ top: section ? 0 : overviewPosition.current, behavior: "instant" });
    if (section) titleRef.current?.focus({ preventScroll: true });
    else if (lastEntry.current) overviewRef.current?.querySelector(`[data-account-entry="${lastEntry.current}"]`)?.focus({ preventScroll: true });
  }, [section, reviewEntry]);

  const rows = {
    profile: { title: t("profile"), icon: UserRound, sub: l("Stammdaten und Monatskosten", "Personal details and monthly costs") },
    nutrition: { title: t("nutrition"), icon: Utensils, sub: l("Wochenplan prüfen & Ernährungsprofil", "Review your weekly plan & dietary profile") },
    shopping: { title: l("Einkaufsliste", "Shopping list"), icon: ShoppingBasket, sub: l("Zutaten aus deinem Wochenplan", "Ingredients from your weekly plan") },
    inventory: { title: t("myInventory"), icon: PackageCheck, sub: l("Mahlzeiten und Supplements", "Prepared meals and supplements") },
    deliveries: { title: t("deliveries"), icon: Truck, sub: l("Offene und bisherige Lieferungen", "Upcoming and past deliveries") },
    household: { title: t("cookingAndHousehold"), icon: CookingPot, sub: l("Für wen wird mitgekocht?", "Who are you cooking for?") },
    "training-plan": { title: t("trainingPlans"), icon: Dumbbell, sub: training?.upload_requested ? t("uploadTrainingPlan") : l("Wochenplan, Uploads & Versionen", "Weekly plan, uploads & versions") },
    performance: { title: t("myPerformanceHistory"), icon: Activity, sub: t("last30Days") },
    documents: { title: t("documentsAndBilling"), icon: FileArchive, sub: l("Rechnungen, Belege & Downloads", "Statements, receipts & downloads") },
    addresses: { title: t("addressesAndPlaces"), icon: MapPin },
    notifications: { title: t("pushNotifications"), icon: BellRing },
    appearance: { title: t("appearanceAndLanguage"), icon: Languages, sub: `${theme === "light" ? t("light") : t("dark")} · ${lang === "de" ? "Deutsch" : "English"}` },
    security: { title: t("security"), icon: ShieldCheck, sub: t("passwordAndLogout") },
  };
  const content = (key, active) => {
    if (key === "profile") return <div className="space-y-4"><PlayerMonthlyCosts active={active} /><AccountProfileSettings mode="player" theme={theme} setTheme={setTheme} section={key} /></div>;
    if (key === "appearance" || key === "security") return <AccountProfileSettings mode="player" theme={theme} setTheme={setTheme} section={key} />;
    if (key === "nutrition") return <PlayerNutritionAccount section={active ? section : "inactive"} />;
    if (key === "inventory") return <PlayerInventoryAccount user={user} section={active ? section : "inactive"} />;
    if (key === "training-plan") return <PlayerTrainingPlans onChanged={loadTraining} />;
    if (key === "documents") return <PlayerDocumentsBilling pageActive={active} />;
    if (key === "performance") return <PlayerPerformanceHistory user={user} />;
    if (key === "notifications") return <PlayerPushSettings />;
    return null;
  };

  return <div className="pc-player-account" data-testid="account-panel">
    <header className="mb-5">
      {section && <Link to={backHref} onClick={(event) => {
        // Only pop a known in-app entry; direct/deep links retain a safe fallback.
        if (performanceOrigin && location.state?.performanceHistoryEntry && window.history.state?.idx > 0
          && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
          event.preventDefault(); navigate(-1);
        }
      }} className="pc-account-back" data-testid="account-back"><ArrowLeft size={18} aria-hidden="true" />{fromToday ? l("Zurück zu Heute", "Back to Today") : l("Zurück zu Konto", "Back to account")}</Link>}
      <h1 ref={titleRef} tabIndex={-1} className="font-display text-2xl font-semibold" data-testid="account-title">{section ? rows[section].title : t("account")}</h1>
      {!section && <p className="text-sm mt-1" style={{ color: "var(--pc-muted)" }}>{user?.name}</p>}
    </header>
    <div ref={overviewRef} hidden={!!section} className="space-y-6" data-testid="account-overview">
      {ACCOUNT_GROUPS.map((group) => <section key={group.en} data-testid="account-group">
        <h2 className="text-xs font-semibold mb-2" style={{ color: "var(--pc-muted)" }}>{group[lang === "de" ? "de" : "en"]}</h2>
        <div className="pc-card pc-account-group">{group.sections.map((key) => {
          const row = rows[key]; const Icon = row.icon;
          return <Link key={key} to={accountSectionHref(key)} state={key === "performance" ? { performanceOrigin: `${location.pathname}${location.search}`, performanceHistoryEntry: true } : undefined} className="pc-account-row" data-account-entry={key} data-testid={`account-open-${key}`} onClick={() => { overviewPosition.current = window.scrollY; lastEntry.current = key; }}>
            <Icon size={19} aria-hidden="true" />
            <span className="min-w-0 flex-1"><span className="block font-semibold text-sm">{row.title}</span>{row.sub && <span className="pc-account-subline">{row.sub}</span>}</span>
            <ChevronRight size={16} aria-hidden="true" />
          </Link>;
        })}</div>
      </section>)}
    </div>
    {/* Lazy, player-scoped views preserve drafts without duplicate nutrition/stock instances. */}
    {mountedFamilies.map((key) => <div key={key} hidden={family !== key} className="pc-account-detail" data-detail-section={family === key ? section : undefined} data-testid={`account-content-${key}`}>
      {content(key, family === key)}
    </div>)}
  </div>;
}
